import React, { useState, useEffect } from 'react';
import AgreementPicker from './AgreementPicker';

export default function InviteModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [canInvite, setCanInvite] = useState(false);
  const [noAgreement, setNoAgreement] = useState(false);
  const [noAgreementAck, setNoAgreementAck] = useState(false);
  const [copied, setCopied] = useState(false);
  // Whether this company has the self-serve agreement workflow switched on
  // by the platform owner. null = still checking. Off by default for every
  // company — see broker_companies.introducer_agreement_workflow_enabled.
  const [workflowEnabled, setWorkflowEnabled] = useState(null);
  const [form, setForm] = useState({
    email: '',
    introducer_name: '',
    adviser_name: '',
    frn_number: '',
    fee_type: 'percentage',
    fee_value: '',
    fee_applies_to_renewals: false
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/agreement-templates/workflow-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setWorkflowEnabled(!!data.enabled);
        if (!data.enabled) {
          // No self-serve workflow available — invites always go out simple.
          setNoAgreement(true);
          setCanInvite(true);
        }
      } catch (e) {
        // Fail safe to the simple path rather than blocking invites.
        setWorkflowEnabled(false);
        setNoAgreement(true);
        setCanInvite(true);
      }
    })();
  }, [token]);

  const handleNoAgreementToggle = (checked) => {
    setNoAgreement(checked);
    if (checked) {
      setSelectedTemplateId(null);
      setCanInvite(true);
    } else {
      setCanInvite(false); // AgreementPicker will re-evaluate on render
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          template_id: noAgreement ? null : selectedTemplateId,
          agreement_required: !noAgreement,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invite');
      setLink(data.signupLink);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>Invite Introducer</h2>

        {!link ? (
          <>
            {step === 1 && (
              <>
                <p style={styles.sub}>Step 1 of 3 — Introducer details</p>
                <label style={styles.label}>Introducer Name</label>
                <input style={styles.input} placeholder="Their name — used to personalise the invite email"
                  value={form.introducer_name} onChange={e => set('introducer_name', e.target.value)} />
                <label style={styles.label}>Introducer Email *</label>
                <input style={styles.input} type="email" placeholder="introducer@email.com"
                  value={form.email} onChange={e => set('email', e.target.value)} />
                <label style={styles.label}>Handling Adviser (you) *</label>
                <input style={styles.input} placeholder="The adviser who'll handle their referrals"
                  value={form.adviser_name} onChange={e => set('adviser_name', e.target.value)} />
                <label style={styles.label}>FRN Number <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input style={styles.input} placeholder="e.g. 123456"
                  value={form.frn_number} onChange={e => set('frn_number', e.target.value)} />
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '-8px 0 12px' }}>
                  Only needed if the introducer is FCA-registered — leave blank otherwise.
                </p>
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.buttons}>
                  <button onClick={onClose} style={styles.cancel}>Cancel</button>
                  <button onClick={() => {
                    if (!form.email || !form.adviser_name) return setError('Email and adviser name are required');
                    setError('');
                    setStep(2);
                  }} style={styles.submit}>Next →</button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p style={styles.sub}>Step 2 of 3 — Fee arrangement</p>
                <label style={styles.label}>Fee Type</label>
                <div style={styles.toggle}>
                  <button
                    onClick={() => set('fee_type', 'percentage')}
                    style={{ ...styles.toggleBtn, ...(form.fee_type === 'percentage' ? styles.toggleActive : {}) }}>
                    % of Commission
                  </button>
                  <button
                    onClick={() => set('fee_type', 'fixed')}
                    style={{ ...styles.toggleBtn, ...(form.fee_type === 'fixed' ? styles.toggleActive : {}) }}>
                    Fixed Fee
                  </button>
                </div>
                <label style={styles.label}>
                  {form.fee_type === 'percentage' ? 'Percentage (%)' : 'Fixed Fee (£)'}
                </label>
                <input style={styles.input}
                  type="text" inputMode="decimal"
                  placeholder={form.fee_type === 'percentage' ? 'e.g. 25' : 'e.g. 500'}
                  value={form.fee_value} onChange={e => set('fee_value', e.target.value.replace(/[^0-9.]/g, ''))} />
                <div style={styles.checkRow}>
                  <input type="checkbox" id="renewals" checked={form.fee_applies_to_renewals}
                    onChange={e => set('fee_applies_to_renewals', e.target.checked)} />
                  <label htmlFor="renewals" style={styles.checkLabel}>Fee applies to renewals</label>
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '-6px 0 12px' }}>
                  ℹ️ This fee arrangement is shown to the introducer in their invite email.
                </p>
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.buttons}>
                  <button onClick={() => setStep(1)} style={styles.cancel}>← Back</button>
                  <button onClick={() => {
                    if (!form.fee_value) return setError('Please enter a fee value');
                    setError('');
                    setStep(3);
                  }} style={styles.submit}>Next →</button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p style={styles.sub}>Step 3 of 3 — Introducer agreement</p>

                {workflowEnabled === null && (
                  <p style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>Checking agreement setup…</p>
                )}

                {workflowEnabled === false && (
                  <>
                    <div style={{
                      background: '#fffbeb', border: '1px solid #fcd34d',
                      borderRadius: 10, padding: 16,
                    }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
                        <div>
                          <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 13, color: '#92400e' }}>
                            This invite will go out without a digital agreement
                          </p>
                          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
                            Your company doesn't have a digital introducer agreement set up yet. Upload your
                            network's introducer agreement document (e.g. Sesame, PRIMIS, Openwork) and we'll get
                            an automated, approved version set up — or email{' '}
                            <a href="mailto:support@brokerconnect.co.uk" style={{ color: '#92400e' }}>support@brokerconnect.co.uk</a> if
                            you'd rather send it that way.
                          </p>
                          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
                            In the meantime, this introducer will still confirm during signup that a paper
                            agreement and lawful basis for data sharing are in place with you directly.
                          </p>
                          <a
                            href="/agreements-status"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: 'inline-block', padding: '7px 14px', borderRadius: 8,
                              background: '#fff', border: '1px solid #92400e', color: '#92400e',
                              fontSize: 12, fontWeight: 600, textDecoration: 'none',
                            }}
                          >
                            Upload your agreement →
                          </a>
                        </div>
                      </div>
                    </div>
                    <label style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, color: '#4b5a6e',
                      margin: '12px 0 0', cursor: 'pointer', lineHeight: 1.5,
                    }}>
                      <input
                        type="checkbox"
                        checked={noAgreementAck}
                        onChange={e => setNoAgreementAck(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      I understand this introducer won't sign a digital agreement through BrokerConnect — I'll
                      arrange a paper agreement with them directly.
                    </label>
                  </>
                )}

                {workflowEnabled === true && (
                  <>
                    {/* No agreement toggle */}
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '14px 16px', borderRadius: 10, marginBottom: 16,
                      background: noAgreement ? '#f0fdf4' : '#f9fafb',
                      border: `1.5px solid ${noAgreement ? '#86efac' : '#e5e7eb'}`,
                      cursor: 'pointer',
                    }} onClick={() => handleNoAgreementToggle(!noAgreement)}>
                      {/* Custom toggle pill */}
                      <div style={{
                        width: 40, height: 22, borderRadius: 999, flexShrink: 0, marginTop: 1,
                        background: noAgreement ? '#10b981' : '#d1d5db',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                        <div style={{
                          position: 'absolute', top: 3, left: noAgreement ? 20 : 3,
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                        }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: noAgreement ? '#166534' : '#374151' }}>
                          No introducer agreement needed
                        </div>
                        <div style={{ fontSize: 12, color: noAgreement ? '#16a34a' : '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
                          {noAgreement
                            ? 'This introducer will skip the agreement gate and log straight in.'
                            : 'Toggle on if you have a separate arrangement in place and do not need a digital agreement.'}
                        </div>
                      </div>
                    </div>

                    {/* Only show AgreementPicker when agreement IS required */}
                    {!noAgreement && (
                      <AgreementPicker
                        token={token}
                        value={selectedTemplateId}
                        onChange={setSelectedTemplateId}
                        onCanInvite={setCanInvite}
                      />
                    )}
                  </>
                )}

                {error && <p style={{ ...styles.error, marginTop: 12 }}>{error}</p>}
                <div style={styles.buttons}>
                  <button onClick={() => setStep(2)} style={styles.cancel}>← Back</button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !canInvite || (workflowEnabled === false && !noAgreementAck)}
                    style={{ ...styles.submit,
                      opacity: (!canInvite || loading || (workflowEnabled === false && !noAgreementAck)) ? 0.5 : 1,
                      cursor: (!canInvite || loading || (workflowEnabled === false && !noAgreementAck)) ? 'not-allowed' : 'pointer' }}>
                    {loading ? 'Sending...' : (workflowEnabled === false ? 'Send invite without agreement' : 'Send Invite')}
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <p style={styles.success}>✅ Invite sent!</p>
            <p style={styles.sub}>
              We've emailed <strong>{form.email}</strong> with everything they need to sign up
              {form.introducer_name ? ` — addressed to ${form.introducer_name.split(' ')[0]}` : ''}.
              No need to forward anything, but you can also share this link directly:
            </p>
            <div style={styles.linkBox}>{link}</div>
            <div style={styles.buttons}>
              <button onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                style={{ ...styles.submit, background: copied ? '#0e1b2c' : '#10b981' }}>
                {copied ? '✓ Copied!' : 'Copy Link'}
              </button>
              <button onClick={onClose} style={styles.cancel}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 32, width: 440, maxWidth: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  title: { margin: '0 0 8px', fontSize: 20, color: '#0e1b2c' },
  sub: { color: '#4b5a6e', fontSize: 13, margin: '0 0 20px' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box', marginBottom: 14 },
  error: { color: '#dc2626', fontSize: 13, margin: '0 0 12px' },
  success: { color: '#10b981', fontWeight: 600, marginBottom: 8 },
  linkBox: { background: '#f3f4f6', borderRadius: 8, padding: '10px 14px', fontSize: 13, wordBreak: 'break-all', marginBottom: 16, color: '#0e1b2c' },
  buttons: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  cancel: { padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 },
  submit: { padding: '9px 18px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  toggle: { display: 'flex', gap: 8, marginBottom: 14 },
  toggleBtn: { flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 },
  toggleActive: { background: '#10b981', color: '#fff', border: '1px solid #10b981' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 },
  checkLabel: { fontSize: 14, color: '#374151' }
};
